<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import Async from '../components/Async.svelte';
  import Alert from '../components/Alert.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import Code from '../components/Code.svelte';
  import DistributionTable from '../components/DistributionTable.svelte';
  import PeriodChips from '../components/PeriodChips.svelte';
  import { getCatalog, getCubes, deleteAggregate } from '../lib/api.js';
  import { CUBE_STATES } from '../../../lib/cube_reconcile.js';
  import { HELP } from '../lib/help.js';
  import { can } from '../lib/session.svelte.js';

  // Building a cube is the analyst's (the backend enforces it).
  const canAdd = $derived(can('author-cubes'));

  const catalog = getCatalog();
  // Two independent reads: the private cubes are on this Pod and always
  // readable, the manifests depend on a partner grant. Failing the first
  // because the second is unreachable would hide the diagnostic exactly when
  // it is most wanted. The cubes promise is awaited in two places below —
  // created once here, so that is one fetch, not two.
  let cubes = $state(getCubes());

  // A cube nobody has been given can be removed here: its entry and its file
  // go, and the rest are counted again. A shared one is withdrawn on Partners
  // first.
  let removing = $state(null);
  let removeError = $state(null);
  async function remove(id) {
    removing = id; removeError = null;
    try { await deleteAggregate(id); cubes = getCubes(); }
    catch (e) { removeError = e.message; }
    finally { removing = null; }
  }

  // Drill-down keys are scoped ("own:x" / "shared:x"), never the bare cube id:
  // a partner publishing a cube with the same name as ours is the normal case,
  // not a clash to be surprised by.
  let open = $state(new Set());
  function toggle(key) {
    const next = new Set(open);
    next.has(key) ? next.delete(key) : next.add(key);
    open = next;
  }

  // Per cube, because more than one can be open and each is being read on its
  // own terms. Survey rounds do not add up (`periodsAddUp` false): no "All
  // periods", and a cube opens on its latest period value. The rule is this
  // institution's, for its partners' cubes too, as on Comparisons.
  let periodBy = $state({});
  const pick = (key, period) => (periodBy = { ...periodBy, [key]: period });
  const periodsOf = (cube) => cube.periods.map((p) => p.period).filter(Boolean);

  // The cells to show for the chip that is selected. "All periods" sums each
  // group across the quarters — see the caveat rendered alongside it.
  function view(cube, selected) {
    if (selected !== 'all') {
      const p = cube.periods.find((x) => x.period === selected);
      return { rows: p?.cells ?? [], total: p?.total ?? 0 };
    }
    const byKey = new Map();
    for (const p of cube.periods) {
      for (const cell of p.cells) {
        const key = cell.values.join(' ');
        const seen = byKey.get(key);
        if (seen) seen.count += cell.count;
        else byKey.set(key, { values: cell.values, count: cell.count });
      }
    }
    const rows = [...byKey.values()].sort((a, b) => b.count - a.count);
    return { rows, total: rows.reduce((s, r) => s + r.count, 0) };
  }

  const publishedList = (to) => to.map((p) => p.label).join(', ');

  // The inbound cubes belonging to the partner this catalog view anchors on.
  // Matched by container URL — both sides get it from the same registry entry,
  // and a label is a display string, not a key.
  // The schemas a cube's terms come from, as people read them.
  const schemasOf = (k, dimensions) => [...new Set((dimensions ?? [])
    .map((d) => k.termSchemas?.[d]).filter(Boolean))].map((label) => `From ${label}`);
  const sharedFor = (k, container) =>
    k.shared.find((s) => s.containers.includes(container)) ?? null;
</script>

<h1 class="wf2-title wf2-title-level-1">Cubes</h1>
<p class="wf2-subtitle">What this Pod counted, who has a copy, and what partners share with you</p>

{#if canAdd}
  <Panel>
    <p class="wf2-text">A comparison can only break down by something a cube already counts.</p>
    <div class="wf2-row"><Button variant="primary" to="catalog/new">Add new</Button></div>
  </Panel>
{/if}

<Async promise={cubes} loading="Reading your private cubes…">
  {#snippet children(k)}
    {@const attention = k.cubes.filter((c) => CUBE_STATES[c.state]?.needsHuman)}

    <Section title="Your cubes">
      <Panel>
        {#if attention.length}
          <Alert title="{attention.length} cube{attention.length === 1 ? '' : 's'} need{attention.length === 1 ? 's' : ''} a decision" variant="warning">
            <ul>
              {#each attention as c}
                <li>
                  <code class="mono">{c.id}</code> — {CUBE_STATES[c.state].note}
                </li>
              {/each}
            </ul>
          </Alert>
        {/if}

        <div class="wf2-tablewrap" style="overflow-x:auto">
          <table class="wf2-table">
            <thead>
              <tr>
                <th>Cube</th><th>Dataset</th><th>Groups</th><th>State</th><th>Shared with</th><th></th>
              </tr>
            </thead>
            <tbody>
              {#each k.cubes as c}
                {@const state = CUBE_STATES[c.state]}
                <tr>
                  <td><span class="mono">{c.id}</span>
                    {#each schemasOf(k, c.declaredDimensions) as s}<span class="col-stats">{s}</span>{/each}</td>
                  <td>{#if c.dataset}<span class="tag {c.dataset}">{c.datasetLabel}</span>{:else}<span class="muted">—</span>{/if}</td>
                  <td>{c.computed ? c.observations : '—'}</td>
                  <td>
                    <span class="tag cube-state {state.tone}" title={state.note}>{state.label}</span>
                  </td>
                  <td>{publishedList(c.publishedTo) || '—'}</td>
                  <td style="white-space:nowrap">
                    {#if c.observations}
                      <button class="link" onclick={() => toggle(`own:${c.id}`)}>
                        {open.has(`own:${c.id}`) ? 'Hide numbers' : 'Show numbers'}
                      </button>
                    {/if}
                    {#if canAdd && c.declared && c.sharing === 'none' && !c.publishedTo.length}
                      <Button variant="ghost" onclick={() => remove(c.id)} disabled={Boolean(removing)}>{removing === c.id ? 'Removing…' : 'Remove'}</Button>
                    {/if}
                  </td>
                </tr>
                {#if open.has(`own:${c.id}`)}
                  {@render drill({
                    key: `own:${c.id}`, cube: c, colspan: 6, measure: c.measure, note: state.note,
                    declared: c.declared ? c.declaredDimensions : null,
                    declaredBy: 'you asked it to count', addUp: k.periodsAddUp,
                  })}
                {/if}
              {/each}
            </tbody>
          </table>
        </div>

        {#if removeError}<Alert title="Could not remove" variant="danger"><p>{removeError}</p></Alert>{/if}
        {#if canAdd && k.cubes.some((c) => c.sharing !== 'none' || c.publishedTo.length)}
          <p class="wf2-caption">A cube that is shared is withdrawn on Partners before it can be removed.</p>
        {/if}
        <p class="wf2-caption">
          Small groups were left out before each file was written, under its
          dataset's rules (on the dataset's Sharing tab), so a missing group is not a zero.
        </p>
      </Panel>
    </Section>
  {/snippet}
</Async>

<Async promise={catalog} loading="Reading manifests…">
  {#snippet children(c)}
    {@const example = c.own.datasets[0]}

    <HelpDrawer {...HELP.catalog}>
      {#snippet children()}
        {#if example}
          <p class="help-prose">An entry in your manifest:</p>
          <Code lang="turtle" text={`<#${example.dataSet}> a void:Dataset, qb:DataSet ;
  dcterms:publisher "${c.own.publisher}" ;
  core:inDataset "${example.dataset}" ;
  core:datasetLabel "${example.datasetLabel}" ;
  void:entities ${example.entities} ;
  core:measure "${example.measure}" ;
  core:dimension ${example.dimensions.map((d) => `"${d}"`).join(', ')} .`} />
        {/if}
      {/snippet}
    </HelpDrawer>

    <!-- Where our own cubes went is the "Published to" column above; a second
         table of the outbound copies said the same thing twice. What is left
         here is the direction the first table cannot see: what came in. -->
    <Section title="Shared with you by {c.partner.publisher}">
      <Panel>
        {#if c.partner.status === 200}
          <Async promise={cubes} loading="Reading their cubes…">
            {#snippet children(k)}
              {@const bank = sharedFor(k, c.partner.container)}
              {@const listed = new Set(c.partner.datasets.map((d) => d.dataSet))}
              {@const unlisted = (bank?.cubes ?? []).filter((x) => !listed.has(x.id))}
              <div class="wf2-tablewrap" style="overflow-x:auto">
                <table class="wf2-table">
                  <thead>
                    <tr>
                      <th>Cube</th><th>Dataset</th><th>Groups</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each c.partner.datasets as d}
                      {@const key = `shared:${d.dataSet}`}
                      {@const file = bank?.cubes.find((x) => x.id === d.dataSet) ?? null}
                      <tr>
                        <td><span class="mono">{d.dataSet}</span>
                          {#each schemasOf(k, d.dimensions) as s}<span class="col-stats">{s}</span>{/each}</td>
                        <td>{#if d.dataset}<span class="tag {d.dataset}">{d.datasetLabel}</span>{:else}<span class="muted">—</span>{/if}</td>
                        <td>
                          {d.entities}
                          {#if file && file.observations !== d.entities}
                            <span class="muted">({file.observations} in the file)</span>
                          {/if}
                        </td>
                        <td>
                          {#if file?.observations}
                            <button class="link" onclick={() => toggle(key)}>
                              {open.has(key) ? 'Hide numbers' : 'Show numbers'}
                            </button>
                          {/if}
                        </td>
                      </tr>
                      {#if file && open.has(key)}
                        {@render drill({
                          key, cube: file, colspan: 4, measure: d.measure,
                          note: `${c.partner.publisher} counted these and decided what was too small to include, not you — this is the file exactly as it arrived.`,
                          // Their manifest lists the period among the dimensions;
                          // the reader classifies it as the period, so drop it
                          // before comparing or every cube looks divergent.
                          declared: d.dimensions.filter((x) => x !== k.period),
                          declaredBy: `${c.partner.publisher} said it counts`, addUp: k.periodsAddUp,
                        })}
                      {/if}
                    {/each}
                  </tbody>
                </table>
              </div>

              {#if unlisted.length}
                <p class="wf2-hint">
                  {unlisted.length} cube{unlisted.length === 1 ? '' : 's'} they share with you
                  {unlisted.length === 1 ? 'is' : 'are'} not in their own description of what
                  they share (<code class="mono">{unlisted.map((x) => x.id).join(', ')}</code>).
                  You can read {unlisted.length === 1 ? 'it' : 'them'}, but nobody has told you
                  what {unlisted.length === 1 ? 'it is' : 'they are'} — a description is a claim
                  about what was sent, and the sending can differ from the claim.
                </p>
              {/if}
            {/snippet}
          </Async>

        {:else}
          <p class="state error">⚠ Can't read this ({c.partner.status || 'no response'}) — they haven't shared anything with you yet.</p>
        {/if}
      </Panel>
    </Section>

  {/snippet}
</Async>

<!-- One cube's numbers, read the same way wherever they came from: quarter
     picker, one row per group, count and share. `declared` is the other half of
     the comparison — aggregations.json for our cubes, the partner's manifest for
     theirs — and is only rendered when it disagrees with the file. -->
{#snippet drill({ key, cube, colspan, measure, note, declared = null, declaredBy = '', addUp = true })}
  {@const selected = periodBy[key] ?? (addUp ? 'all' : periodsOf(cube).sort().at(-1) ?? 'all')}
  {@const shown = view(cube, selected)}
  <tr>
    <td {colspan} class="drill">
      {#if note}<p class="wf2-caption">{note}</p>{/if}
      {#if declared && declared.join() !== cube.dimensions.join()}
        <p class="wf2-hint">
          <strong>These don't match.</strong> The file actually counts
          <code class="mono">{cube.dimensions.join(', ')}</code>, but
          {declaredBy} <code class="mono">{declared.join(', ') || '—'}</code>.
          The numbers below are what the file really holds — trust those, and
          find out why the two disagree.
        </p>
      {/if}

      <PeriodChips periods={periodsOf(cube)} {selected} all={addUp} onselect={(p) => pick(key, p)} />

      <h4 class="wf2-title wf2-title-level-4">
        {selected === 'all' ? 'All periods' : selected} · {shown.total}
        <span class="wf2-caption">{measure}</span>
      </h4>
      <DistributionTable columns={cube.dimensions} rows={shown.rows} total={shown.total} />

      {#if selected === 'all' && cube.periods.length > 1}
        <p class="wf2-hint">
          Added up across {cube.periods.length} periods. A group left out of one
          quarter for being too small is missing from that quarter's total, so
          the real figure is <strong>this or higher</strong> — never lower. Pick
          a single quarter if you need a number you can stand behind.
        </p>
      {/if}
      {#if cube.dimensions.length > 1}
        <p class="wf2-hint">
          <strong>Careful adding these up.</strong> This cube counts
          {cube.dimensions.length} things in combination, and every combination
          too small to include was left out. So adding the rows together to get a
          total for just one of them gives you a number that is
          <strong>too low</strong>, and nothing on the page will warn you. For a
          total of one thing on its own, read the cube that counts that one thing
          on its own.
        </p>
      {/if}
    </td>
  </tr>
{/snippet}

<style>
  /* Three tones, because the six states are not six degrees of the same thing:
     four are normal outcomes, two mean someone has to go and look. */
  .cube-state.ok {
    background: color-mix(in srgb, var(--accent), transparent 82%);
    border-color: color-mix(in srgb, var(--accent), transparent 40%);
  }
  .cube-state.warn {
    background: color-mix(in srgb, var(--warn), transparent 82%);
    border-color: color-mix(in srgb, var(--warn), transparent 40%);
  }
  .cube-state.error {
    background: color-mix(in srgb, var(--danger), transparent 85%);
    border-color: color-mix(in srgb, var(--danger), transparent 40%);
    color: var(--danger);
  }
  .link {
    background: none; border: none; padding: 0;
    color: var(--accent); font: inherit; cursor: pointer; text-decoration: underline;
  }
  .drill { background: var(--surface-2); }
</style>
